import React, { useState, useRef, useEffect } from "react";
import SkeletonLoader from "./SkeletonLoader";
import noPreviewImage from "../../Assets/photo/no-preview-available.webp";

/**
 * LoadingVideo Component
 * Displays video with loading skeleton, error handling, and fallback support
 *
 * @param {string} src - Video source URL
 * @param {string} type - MIME type (e.g., "video/mp4")
 * @param {string} fallback - Fallback image (default: no-preview-available.webp)
 * @param {number} width - Video width (default: 100%)
 * @param {number} height - Video height (default: auto)
 * @param {string} className - Additional CSS classes
 * @param {object} style - Inline styles
 * @param {function} onLoad - Callback when video loads successfully
 * @param {function} onError - Callback when video fails to load
 * @param {boolean} autoPlay - Auto play (default: false)
 * @param {boolean} loop - Loop (default: false)
 * @param {boolean} muted - Muted (default: false)
 * @param {boolean} controls - Show controls (default: true)
 * @param {...props} - Additional video element props
 */
const LoadingVideo = ({
  src,
  type = "video/mp4",
  fallback = noPreviewImage,
  width = "100%",
  height = "auto",
  className = "",
  style = {},
  onLoad = () => {},
  onError = () => {},
  autoPlay = false,
  loop = false,
  muted = false,
  controls = true,
  skeletonHeight = "200px",
  showFallbackOnError = true,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
  }, [src]);

  const handleLoadedData = () => {
    setIsLoading(false);
    setHasError(false);
    onLoad();
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
    onError();
  };

  const handleCanPlay = () => {
    setIsLoading(false);
    setHasError(false);
  };

  return (
    <div
      style={{
        width,
        height,
        display: "inline-block",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#000",
        ...style,
      }}
      className={className}
    >
      {isLoading && (
        <SkeletonLoader
          width="100%"
          height={skeletonHeight}
          borderRadius={style.borderRadius || "0px"}
        />
      )}

      {hasError && showFallbackOnError ? (
        <img
          src={fallback}
          alt="Video unavailable"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        <video
          ref={videoRef}
          onLoadedData={handleLoadedData}
          onCanPlay={handleCanPlay}
          onError={handleError}
          autoPlay={autoPlay}
          loop={loop}
          muted={muted}
          controls={controls}
          style={{
            width: "100%",
            height: "100%",
            display: isLoading || hasError ? "none" : "block",
            ...style,
          }}
          {...props}
        >
          {src && <source src={src} type={type} />}
          Your browser does not support the video tag.
        </video>
      )}
    </div>
  );
};

export default LoadingVideo;
